import { action, comparer, computed, makeObservable, observable, reaction } from 'mobx';
import moment from 'moment-timezone';
import api from 'services/api';
import type { Recurrence } from 'api/recurrence_definitions/endpoints/types';
import type { SchedulingWindowPeriod } from 'api/recurrence/endpoints/fetchFirstOccurrence';
import { computeFirstOccurrence } from 'api/recurrence/operations/computeFirstOccurrence';
import { computeOccurrenceSeries } from 'api/recurrence/operations/computeOccurrenceSeries';
import { mapAvailablePeriods } from 'api/recurrence/operations/mapAvailablePeriods';
import { mergeAvailablePeriods } from 'api/recurrence/operations/mergeAvailablePeriods';
import { resolveRecurrenceDefinitionForSubmit } from 'api/recurrence_definitions/operations/resolveRecurrenceDefinitionForSubmit';
import type { EditableRecurrenceDefinition } from 'pages/apps/platform/recurrence_definitions/edit/EditableRecurrenceDefinition';
import type { RecurrenceMode } from 'pages/apps/platform/recurrence_definitions/edit/EditableRecurrence';
import { createManualDefinition, manualRule, manualWireRecurrences } from './manualRecurrence';

/**
 * Debounce before recomputing the first occurrence. The inputs change per
 * keystroke while an interval or a datetime is being typed; one request per
 * settled edit is enough.
 */
export const FIRST_OCCURRENCE_DEBOUNCE_MS = 400;

/**
 * How the operator wants the start time decided.
 *
 * - `asap` — let the schedule decide: now, or the next window opening, or the
 *   next occurrence of an anchored recurrence. The application owns the value.
 * - `requested` — the operator names a datetime and the application NEVER
 *   rewrites it. A scheduling window may still move the instance later at
 *   submit time (the backend snaps a closed window forward), and an anchored
 *   recurrence's own start_time applies from the SECOND occurrence on, so the
 *   first interval can differ from the rule's interval.
 */
export type StartIntent = 'asap' | 'requested';

/**
 * Where the submit's schedule comes from — one exclusive choice, so the form can
 * offer it as a single radio group.
 *
 * - `none` — a one-off submit; no recurrence is sent at all.
 * - `definition` — a saved recurrence definition, submitted live by id.
 * - `interval` / `window_open` — the ad-hoc rule, in that mode.
 *
 * The last two are {@link RecurrenceMode} values rather than a single `manual`:
 * the ad-hoc rule's mode is part of the same choice for the operator, and
 * flattening it keeps the radio group one level deep. The rule's own `mode`
 * field stays the authority — {@link SubmitScheduleModel.scheduleSource} reads it
 * back rather than storing a second copy.
 */
export type ScheduleSource = 'none' | 'definition' | RecurrenceMode;

/**
 * The stored half of {@link ScheduleSource}: which kind of schedule is in
 * effect, with the ad-hoc rule's mode left to the rule itself.
 */
type ScheduleSourceKind = 'none' | 'definition' | 'manual';

/**
 * What the submit will actually do, as far as the schedule can be known.
 *
 * At most one of these holds at a time, so it is one union rather than
 * independent fields: an inconsistent combination is unrepresentable.
 */
export type SchedulePreview =
  | {
      readonly kind: 'ready';
      /**
       * Where a closed scheduling window pushes the start and what it was pushed
       * from, or null when the start stands as the form says. A window is the only
       * thing that moves it: a recurrence's own start_time applies from the second
       * occurrence on, and the first instance may begin at any time.
       *
       * One nullable object rather than three fields so a half-stated
       * postponement is unrepresentable — the destination, the origin and the
       * window that caused it are known together or not at all. `startsAt`
       * derives from `to`, so the unmoved case stays live against the form.
       *
       * `byWindow` may differ from the current selection: it names the window
       * that was in effect when this answer was calculated.
       */
      readonly postponed: { readonly from: Date; readonly to: Date; readonly byWindow: string } | null;
      /** The occurrences after the first; empty for a one-off submit. */
      readonly occurrences: readonly Date[];
    }
  /** The window has no upcoming opening, so nothing can be scheduled at all. */
  | { readonly kind: 'unschedulable'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

/** How many following occurrences the preview asks for. */
const PREVIEW_OCCURRENCE_COUNT = 3;

/**
 * The backend flags "no time this could ever run" with this code on its 400,
 * which is what lets the panel say so plainly instead of reporting a failure the
 * operator might reasonably retry. It does not block the submit: the backend
 * refuses that on its own, and this is a preview.
 */
const NO_OPENING_CODE = 'no_upcoming_opening';

/**
 * Fallback for a backend that predates the code. The two repositories deploy
 * independently, so the message match has to stay until every environment is
 * past it; drop it once that is true.
 */
const NO_OPENING_MARKER = 'No upcoming opening';

/**
 * A scheduling window as the Redux list carries it. The id is optional there,
 * so the lookup below skips rows that lack one rather than posting `undefined`.
 */
export interface SchedulingWindowListItem {
  readonly scheduling_window_id?: number | undefined;
  readonly zone_name?: string | null | undefined;
  readonly name?: string | undefined;
  /** The zone the window's openings are defined in; a candidate display zone. */
  readonly timezone?: string | undefined;
}

/**
 * Redux-sourced values the model needs but must not read from the store itself.
 * Refreshed by the view on every render (see `setContext`).
 */
export interface ScheduleContext {
  readonly schedulingWindows: ReadonlyArray<SchedulingWindowListItem>;
  /** Cached LOV rows handed to the recurrence-definition resolver. */
  readonly recurrenceDefinitions: unknown;
  /** moment token for user-facing times, per the 12h/24h preference. */
  readonly displayTimeToken: string;
}

/** The inputs a first-occurrence calculation depends on. */
interface FirstOccurrenceRequest {
  readonly recurrences: readonly Recurrence[];
  readonly schedulingWindow: string;
  readonly timezone: string;
  /** The operator's datetime under `requested`; null when the schedule decides. */
  readonly requestedStart: string | null;
}

/** Shared empty rule list, so a one-off submit's `recurrences` is a stable value. */
const NO_RECURRENCES: readonly Recurrence[] = [];

const EMPTY_CONTEXT: ScheduleContext = {
  schedulingWindows: [],
  recurrenceDefinitions: [],
  displayTimeToken: 'HH:mm',
};

/**
 * Scheduling state for the node Submit form: start datetime, timezone,
 * instance scheduling window, the recurrence in effect (a picked saved
 * definition or the manual ad-hoc rule), the eligible-period list backing the
 * datetime picker, and the preview of what the submit will actually do.
 *
 * Everything else on the Submit form — parameters, queue, hold-on-submit, note,
 * start node — stays in the component; it does not interact with any of this.
 *
 * Redux-sourced values arrive through `setContext`: the model must not read the
 * store, so the view hands them in.
 */
export class SubmitScheduleModel {
  /** The manual ad-hoc rule holder; stable for the model's lifetime. */
  readonly manualDefinition: EditableRecurrenceDefinition = createManualDefinition();

  /** How the start time is decided; see {@link StartIntent}. */
  intent: StartIntent = 'asap';

  /** Instance-level scheduling window key ("zone|name"), or "" for none. */
  schedulingWindow = '';

  /**
   * The saved recurrence definition picked in the form, by id, or "" for none.
   *
   * A picked id is remembered while another source is in effect, so switching
   * away from `definition` and back does not lose the selection. Whether it is
   * actually submitted is {@link scheduleSource}'s decision, not this field's.
   */
  selectedRecurrenceId = '';

  /**
   * The operator's requested start, as an offset-free wall-clock string.
   *
   * Written by the operator, by a resubmit seed, and by explicitly accepting a
   * suggested value — never by a calculation. Only meaningful under `requested`.
   */
  requestedDatetime: string;

  /**
   * The zone the operator picked for the start datetime, or null to read it in
   * the browser's zone.
   *
   * Nullable so a searchable picker can hold an empty input while the operator
   * types: bound to a never-null value, the fallback would snap back on every
   * keystroke. Everything that computes with or sends the zone reads
   * {@link effectiveTimezone} instead.
   */
  timezone: string | null;

  private _availablePeriods: readonly SchedulingWindowPeriod[] = [];
  /**
   * The start the schedule produced, as an instant; written only by the
   * reaction's effect.
   *
   * An instant, not a wall-clock string, because the backend answers with one
   * (`first_occurrence` is a `toISOString()`) and every consumer wants it back
   * as one. Round-tripping it through a wall-clock string loses an hour when the
   * local reading falls in a repeated DST hour, since two instants share that
   * reading and re-parsing resolves to the earlier.
   */
  private _computedInstant: Date;
  private _context: ScheduleContext = EMPTY_CONTEXT;
  /** Invalidates in-flight definition loads so a superseded pick cannot land. */
  private _definitionLoadSeq = 0;
  private readonly _debounceMs: number;
  private _disposeFirstOccurrence: (() => void) | null = null;
  private _disposeWindowHorizon: (() => void) | null = null;
  /** Invalidates in-flight first-occurrence calls; guards out-of-order responses. */
  private _firstOccurrenceSeq = 0;
  private _isWindowLoading = false;
  private _preview: SchedulePreview = { kind: 'ready', postponed: null, occurrences: [] };
  /**
   * When the schedule behind `_preview` was last asked, or null when nothing has
   * been asked yet.
   *
   * Deliberately NOT part of `_previewFor`: that value is compared structurally
   * against the current request to derive `isPreviewStale`, and a timestamp
   * inside it would differ on every recomputation, pinning the panel
   * permanently stale.
   */
  private _previewAsOf: Date | null = null;
  /** The request `_preview` was computed for; drives `isPreviewStale`. */
  private _previewFor: FirstOccurrenceRequest | null = null;
  /**
   * The zone the operator explicitly chose to read the preview in, or null while
   * it follows the schedule's own zones.
   *
   * Kept apart from {@link timezone} on purpose: that one decides what the
   * schedule MEANS and is sent to the backend, this one only decides how an
   * already-decided answer is displayed. An explicit pick sticks — it is not
   * re-derived when the recurrence or window changes, because the operator asked
   * for that angle.
   */
  private _previewTimezone: string | null = null;
  private _pickedRecurrences: readonly Recurrence[] = [];
  /** In-flight first-occurrence calls, and who is waiting for them to finish. */
  private _running = 0;
  private _scheduleSourceKind: ScheduleSourceKind = 'none';
  private _settledWaiters: Array<() => void> = [];

  constructor(debounceMs: number = FIRST_OCCURRENCE_DEBOUNCE_MS) {
    this.timezone = null;
    // Offset-free wall clock, like every other write to these fields. An
    // offset-bearing value would be read three different ways: the picker
    // strips it, the calculation honours it, and submit reinterprets it in the
    // selected timezone — three different instants for one unedited field.
    this.requestedDatetime = this.nowString();
    this._computedInstant = new Date();

    makeObservable<
      SubmitScheduleModel,
      | '_availablePeriods'
      | '_computedInstant'
      | '_firstOccurrenceRequest'
      | '_isWindowLoading'
      | '_pickedRecurrences'
      | '_preview'
      | '_previewAsOf'
      | '_previewFor'
      | '_previewTimezone'
      | '_scheduleSourceKind'
    >(this, {
      _availablePeriods: observable.ref,
      _computedInstant: observable.ref,
      _isWindowLoading: observable,
      _preview: observable.ref,
      _previewAsOf: observable.ref,
      _previewFor: observable.ref,
      _previewTimezone: observable,
      _pickedRecurrences: observable.ref,
      _scheduleSourceKind: observable,
      intent: observable,
      schedulingWindow: observable,
      selectedRecurrenceId: observable,
      requestedDatetime: observable,
      timezone: observable,
      recurrences: computed,
      _firstOccurrenceRequest: computed({ equals: comparer.structural }),
    });

    this._debounceMs = debounceMs;
    this.start();
  }

  // ── Accessors ─────────────────────────────────────────────────────────────

  /**
   * Everything the first-occurrence calculation depends on, or null when there
   * is nothing to calculate (no recurrence and no window).
   *
   * Safe to track every input: the effect writes only `_computedInstant`, which
   * is deliberately not read here, so the reaction cannot re-trigger itself. That
   * is the reason the requested and computed starts are separate observables
   * rather than one field with two writers.
   */
  private get _firstOccurrenceRequest(): FirstOccurrenceRequest | null {
    const recurrences = this.recurrences;
    const schedulingWindow = this.schedulingWindow;
    if (recurrences.length === 0 && !schedulingWindow) {
      return null;
    }
    return {
      recurrences,
      schedulingWindow,
      timezone: this.effectiveTimezone,
      requestedStart: this.intent === 'requested' ? this.requestedDatetime : null,
    };
  }

  /** Eligible open periods backing the datetime picker and Opening periods list. */
  get availablePeriods(): readonly SchedulingWindowPeriod[] {
    return this._availablePeriods;
  }

  /**
   * Whether "skip when window closed" has anything to decide.
   *
   * It settles skip-versus-postpone for the scheduling window set on this submit
   * — the node instance's own window, {@link schedulingWindow}. With no such
   * window selected there is no closed-window case for it to answer, so the form
   * offers it as read-only rather than letting the operator set a flag that
   * cannot take effect. It does **not** control recurrence
   * `during_scheduling_window` (that constraint always grid-skips when closed).
   *
   * Also requires the ad-hoc rule to be in effect, for the reason every setting
   * in that group does — see {@link usesManualRule}.
   */
  get canSkipWhenWindowClosed(): boolean {
    return this.usesManualRule && this.schedulingWindow !== '';
  }

  /** Tooltip text for the datetime field; null when the value is unparseable. */
  get datetimeTooltip(): string | null {
    if (!this.requestedDatetime) {
      return null;
    }
    const parsed = moment.tz(this.requestedDatetime, this.effectiveTimezone);
    if (!parsed.isValid()) {
      return null;
    }
    return `${parsed.format(`dddd, MMMM D, YYYY [at] ${this._context.displayTimeToken}`)} (${parsed.format('z')})`;
  }

  /**
   * The start datetime that will actually be submitted, as an offset-free
   * wall-clock string in {@link effectiveTimezone}.
   *
   * The operator's value under `requested`, the schedule's under `asap`. Keeping
   * the two apart is what lets the operator switch back and forth without their
   * entry being overwritten by a calculation.
   *
   * Wall-clock because that is the currency of the things that consume it — the
   * submit body and the eligible-period horizon. The PREVIEW does not use this;
   * it deals in instants, via {@link startsAt}.
   */
  get finalScheduled(): string {
    return this.intent === 'requested' ? this.requestedDatetime : moment(this._computedInstant).tz(this.effectiveTimezone).format('YYYY-MM-DDTHH:mm:ss');
  }

  /** The timezone in effect: the operator's pick, else the browser's zone. */
  get effectiveTimezone(): string {
    return this.timezone ?? moment.tz.guess();
  }

  get isWindowLoading(): boolean {
    return this._isWindowLoading;
  }

  /**
   * When the instance will actually start, as an instant — the preview's
   * headline value and the occurrence series' anchor.
   *
   * Derived rather than stored: with no recurrence and no window nothing is
   * calculated at all, so a stored snapshot would go stale the moment the
   * operator edited the field. That is why it is a getter rather than a field on
   * {@link SchedulePreview} — the panel must follow the form live while no
   * calculation is pending.
   *
   * Null only when the operator's requested value cannot be parsed, which the
   * datetime field itself prevents; a resubmit seed carrying a malformed
   * datetime is the reachable path.
   */
  get startsAt(): Date | null {
    const postponed = this._preview.kind === 'ready' ? this._preview.postponed : null;
    if (postponed) {
      return postponed.to;
    }
    return this.intent === 'requested' ? this.requestedInstant : this._computedInstant;
  }

  /**
   * The operator's requested wall-clock resolved against the zone the schedule
   * is calculated in.
   *
   * This is the ONE place a wall-clock value becomes an instant. It reads
   * {@link effectiveTimezone} — the zone the value was entered in — and must
   * never read whatever zone the preview is being *displayed* in, or the
   * operator's own entry would shift as they looked at it from another angle.
   */
  private get requestedInstant(): Date | null {
    const parsed = moment.tz(SubmitScheduleModel.stripOffset(this.requestedDatetime), this.effectiveTimezone);
    return parsed.isValid() ? parsed.toDate() : null;
  }

  /** Whether a recomputation is pending, so the panel can say it is working. */
  get isPreviewStale(): boolean {
    return !comparer.structural(this._previewFor, this._firstOccurrenceRequest);
  }

  /**
   * Whether the start is simply "whenever this is submitted".
   *
   * Under `asap` with no recurrence and no window there is nothing to calculate
   * and nothing to wait for, so a datetime would be false precision: it would
   * show the clock as it stood when the form opened and never move, on the one
   * screen where no calculation exists to timestamp or refresh. What actually
   * happens is that the backend starts it at the instant it receives the submit.
   */
  get startsOnSubmit(): boolean {
    return this.intent === 'asap' && this._firstOccurrenceRequest === null;
  }

  get preview(): SchedulePreview {
    return this._preview;
  }

  /**
   * The timezone the preview is rendered in.
   *
   * Presentation only. Nothing here reaches the submit body or the calculation,
   * so switching it cannot alter a single scheduled time — which is precisely
   * what carrying the preview as instants bought: reading an offset-free wall
   * clock in another zone would reinterpret it instead of converting it.
   *
   * Distinct from {@link timezone}, the zone the schedule is COMPUTED in. Two
   * different questions that happened to share one field before.
   */
  get previewTimezone(): string {
    return this._previewTimezone ?? this.defaultPreviewTimezone;
  }

  /**
   * The zone the operator explicitly chose, or null while the preview follows the
   * schedule's own zones.
   *
   * Separate from {@link previewTimezone} because a picker needs to distinguish
   * "nothing chosen" from "chosen": bind a control to the resolved value instead
   * and it can never be empty, so clearing the field to type a new zone resolves
   * straight back to the default and the entry snaps away mid-keystroke.
   */
  get previewTimezoneSelection(): string | null {
    return this._previewTimezone;
  }

  /**
   * The zone to read the preview in until the operator says otherwise.
   *
   * Ordered most-specific-authority first. A rule's `start_time_zone` pins a
   * wall-clock time somebody deliberately wrote down, so it is the reading most
   * worth confirming at a glance; failing that, the window whose openings decide
   * the timing; failing that, the zone the form is set to, which always resolves
   * because {@link effectiveTimezone} falls back to the browser's guess.
   *
   * A definition may own several rules with different zones, in which case there
   * is no single zone the schedule is "about" and the next candidate wins.
   */
  private get defaultPreviewTimezone(): string {
    const ruleZones = new Set(this.recurrences.map((rule) => rule.start_time_zone).filter((zone): zone is string => !!zone));
    if (ruleZones.size === 1) {
      return [...ruleZones][0];
    }
    return this.selectedWindowTimezone ?? this.effectiveTimezone;
  }

  /** The selected window's own zone, or null when none is selected or known. */
  private get selectedWindowTimezone(): string | null {
    if (!this.schedulingWindow) {
      return null;
    }
    const [zone, windowName] = this.schedulingWindow.split('|');
    const match = this._context.schedulingWindows.find((candidate) => candidate.zone_name === zone && candidate.name === windowName);
    return match?.timezone || null;
  }

  /**
   * When the schedule behind the preview was last asked, or null when it has not
   * been asked yet.
   *
   * The panel reports this rather than pretending to be live. Nothing here
   * tracks the clock — under `asap` the answer is anchored to the instant it was
   * calculated — so how old that instant is, is the operator's to judge, and
   * `refreshNow` is theirs to call.
   */
  get previewAsOf(): Date | null {
    return this._previewAsOf;
  }

  /**
   * The start datetime as the picker wants it: "naive", with any offset or Z
   * stripped so the wall-clock reading is preserved.
   */
  get pickerValue(): moment.Moment {
    if (!this.requestedDatetime) {
      return moment();
    }
    return moment(SubmitScheduleModel.stripOffset(this.requestedDatetime));
  }

  /**
   * The rules currently in effect, per {@link scheduleSource}: a picked
   * definition's, the ad-hoc rule's (empty until it is complete), or none.
   *
   * Computed: read several times per render and by every async path.
   */
  get recurrences(): readonly Recurrence[] {
    switch (this._scheduleSourceKind) {
      case 'definition':
        return this._pickedRecurrences;
      case 'manual':
        return manualWireRecurrences(this.manualDefinition);
      case 'none':
        return NO_RECURRENCES;
    }
  }

  /**
   * The schedule source as the form's radio group sees it — with the ad-hoc
   * rule's own mode standing in for the manual case.
   */
  get scheduleSource(): ScheduleSource {
    return this._scheduleSourceKind === 'manual' ? manualRule(this.manualDefinition).mode : this._scheduleSourceKind;
  }

  /**
   * Whether the ad-hoc rule is the schedule in effect.
   *
   * The form disables the rule's schedule-wide settings on this: they are
   * carried by the ad-hoc snapshot only, so under a picked definition (which
   * brings its own) or a one-off (which sends no schedule at all) they have
   * nothing to act on.
   */
  get usesManualRule(): boolean {
    return this._scheduleSourceKind === 'manual';
  }

  // ── Methods ───────────────────────────────────────────────────────────────

  /**
   * Arms the recomputation and returns a teardown, so mounting and unmounting
   * are symmetric.
   *
   * Re-arming an already-armed model is safe, and has to be: React StrictMode
   * mounts, tears down and mounts again, so a subscription created once at
   * construction and disposed by the first teardown would never come back.
   */
  start = (): (() => void) => {
    this.dispose();
    // Every input that can change the first occurrence — the rules, the window,
    // the timezone, and (under `requested`) the operator's datetime — feeds this
    // one derivation, so there is a single place that decides when to recompute.
    // Structural equality keeps it quiet when a recompute yields the same
    // request; the delay collapses a burst of keystrokes into one call.
    this._disposeFirstOccurrence = reaction(
      () => this._firstOccurrenceRequest,
      (request) => {
        void this.runFirstOccurrence(request);
      },
      { delay: this._debounceMs, equals: comparer.structural }
    );
    // The eligible-period horizon depends on the window and on where the
    // selection sits, nothing else. Tracking those and reading the loaded
    // horizon inside the effect keeps it from re-triggering on its own writes.
    this._disposeWindowHorizon = reaction(
      () => ({ schedulingWindow: this.schedulingWindow, base: this.finalScheduled, timezone: this.effectiveTimezone }),
      () => {
        void this.refreshSchedulingWindow();
      },
      { delay: this._debounceMs, equals: comparer.structural }
    );
    return this.dispose;
  };

  dispose = (): void => {
    this._disposeFirstOccurrence?.();
    this._disposeFirstOccurrence = null;
    this._disposeWindowHorizon?.();
    this._disposeWindowHorizon = null;
  };

  /**
   * The operator picked or typed a start datetime. Only reachable under
   * `requested`, where the value is theirs: calculations compare against it and
   * report, never overwrite.
   */
  handleDatetimeChange = (picked: moment.Moment): void => {
    const current = moment(this.requestedDatetime);
    const dateChanged = !picked.isSame(current, 'day');
    const timeChanged = picked.hour() !== current.hour() || picked.minute() !== current.minute();
    if (!dateChanged && !timeChanged) {
      return;
    }

    this.setRequestedDatetime(picked.clone().seconds(0).milliseconds(0).format('YYYY-MM-DDTHH:mm:ss'));
  };

  /**
   * The operator switched how the start time is decided.
   *
   * Taking ownership seeds their field from what the schedule currently shows,
   * Taking ownership does NOT seed the operator's field from the computed one:
   * whatever they last entered is still theirs, and a switch is not an edit.
   * Handing it back resets the computed value; the reaction refines it when a
   * recurrence or window is in play.
   */
  handleIntentChange = (next: StartIntent): void => {
    if (next === this.intent) {
      return;
    }
    if (next === 'asap') {
      this.setComputedInstant(new Date());
    }
    this.setIntent(next);
    // Nothing has been asked about the new intent yet, so the panel carries no
    // timestamp until the reaction answers for it.
    this.setPreview({ kind: 'ready', postponed: null, occurrences: [] }, this._previewFor, null);
  };

  /**
   * A saved recurrence definition was picked, or the selection was cleared.
   * Under `asap` the anchor returns to now; under `requested` the operator's
   * datetime survives untouched and is only re-reported against.
   *
   * Clearing leaves the schedule source alone: `definition` with nothing picked
   * is a legitimate state (it submits as a one-off), and the operator says
   * "no recurrence" with the radio, not by emptying this field.
   *
   * The eligible-period list is NOT cleared here. It is a function of the
   * scheduling window and an anchor, never of the recurrence — the backend does
   * not pass the recurrence into evaluateSchedulingWindow on any path — so a
   * recurrence change cannot stale it. Clearing it would blank out every day
   * between now and the anchor whenever the anchor sits in the future, because
   * the reloaded list only covers a bounded horizon from that anchor.
   */
  handleRecurrenceDefinitionChange = async (definitionId: number | null): Promise<void> => {
    const loadSeq = ++this._definitionLoadSeq;

    if (definitionId === null) {
      this.setSelectedRecurrenceId('');
      this.setPickedRecurrences([]);
      return;
    }

    this.setSelectedRecurrenceId(String(definitionId));

    try {
      const definition = await resolveRecurrenceDefinitionForSubmit(definitionId, null, this._context.recurrenceDefinitions as never);
      if (loadSeq !== this._definitionLoadSeq) {
        return; // a newer pick (or clear) superseded this load
      }
      this.setPickedRecurrences(definition.recurrences);
    } catch (error: unknown) {
      if (loadSeq !== this._definitionLoadSeq) {
        return;
      }
      this.setPickedRecurrences([]);
      // A failed definition load is not a schedule answer, so it carries no
      // as-of instant; the reaction will produce one once the rules are known.
      this.setPreview({ kind: 'error', message: (error as Error)?.message ?? 'Could not load the selected recurrence definition.' }, this._previewFor, null);
    }
  };

  /**
   * The operator chose where the schedule comes from.
   *
   * Nothing is cleared or seeded: the picked definition and the ad-hoc rule's
   * fields both survive a switch, so toggling and toggling back leaves the form
   * as it was. Only which of them counts changes — see {@link recurrences}.
   *
   * An `action` because the two manual choices write two observables (the kind
   * and the rule's mode); one transaction keeps observers off a half-applied
   * state where the source is manual but the mode is still the old one.
   */
  handleScheduleSourceChange = action((source: ScheduleSource): void => {
    if (source === 'none' || source === 'definition') {
      this._scheduleSourceKind = source;
      return;
    }
    this._scheduleSourceKind = 'manual';
    manualRule(this.manualDefinition).mode = source;
  });

  /**
   * The instance scheduling window was picked or cleared. This IS the event that
   * invalidates the eligible-period list, since the list belongs to the window.
   */
  handleSchedulingWindowChange = (windowKey: string | null): void => {
    this.setSchedulingWindow(windowKey ?? '');
    this.setAvailablePeriods([]);
  };

  /**
   * The operator chose a zone to read the preview in, or cleared the choice so it
   * follows the schedule again.
   *
   * Nothing recomputes: no request, no reaction, no submitted value depends on
   * this. Compare {@link handleTimezoneChange}, which changes what the schedule
   * means and re-asks the backend.
   */
  handlePreviewTimezoneChange = (timezone: string | null): void => {
    this.setPreviewTimezone(timezone);
  };

  /** The start timezone changed; both reactions recompute in the new zone. */
  handleTimezoneChange = (timezone: string | null): void => {
    this.setTimezone(timezone);
  };

  /**
   * Whether a calendar day is selectable. Days beyond the last known close are
   * allowed — the period list only covers a bounded horizon, so anything past
   * it is simply unknown rather than closed.
   */
  isDateValid = (candidate: moment.Moment): boolean => {
    if (this.schedulingWindow === '' || this._availablePeriods.length === 0) {
      return true;
    }

    const timezone = this.effectiveTimezone;
    const candidateLocal = candidate.clone().tz(timezone);
    const candidateDate = candidateLocal.format('YYYY-MM-DD');

    let lastClose: moment.Moment | null = null;
    for (const period of this._availablePeriods) {
      const close = moment.utc(period.close).tz(timezone);
      if (!lastClose || close.isAfter(lastClose)) {
        lastClose = close;
      }
    }

    if (lastClose && candidateLocal.isAfter(lastClose, 'day')) {
      return true;
    }

    return this._availablePeriods.some((period) => moment.utc(period.open).tz(timezone).format('YYYY-MM-DD') === candidateDate);
  };

  /**
   * Extends the eligible-period horizon to cover where the selection now sits.
   * Skips the fetch when the loaded horizon already reaches that far; a window
   * change clears the list first, so this never skips a fresh load.
   */
  private refreshSchedulingWindow = async (): Promise<void> => {
    const windowKey = this.schedulingWindow;
    if (!windowKey) {
      this.setAvailablePeriods([]);
      return;
    }

    this.setWindowLoading(true);

    const [zone, windowName] = windowKey.split('|');
    const match = this._context.schedulingWindows.find((candidate) => candidate.zone_name === zone && candidate.name === windowName);
    if (match?.scheduling_window_id === undefined) {
      this.setWindowLoading(false);
      return;
    }

    const timestamp = moment(SubmitScheduleModel.stripOffset(this.finalScheduled)).tz(this.effectiveTimezone);

    if (this._availablePeriods.length > 0) {
      let lastClose: moment.Moment | null = null;
      for (const period of this._availablePeriods) {
        const close = moment.utc(period.close);
        if (!lastClose || close.isAfter(lastClose)) {
          lastClose = close;
        }
      }
      if (lastClose && timestamp.clone().utc().isSameOrBefore(lastClose)) {
        this.setWindowLoading(false);
        return; // already covered by the known horizon
      }
    }

    try {
      // TODO: move behind an src/api boundary module (see src/api/README.md);
      // this is the last direct api call on the submit path.
      const response = await api.post('/validate-scheduling-window', {
        scheduling_window_id: match.scheduling_window_id,
        timestamp: timestamp.toISOString(),
        timezone: this.effectiveTimezone,
      });

      // Keep the original offset strings as the backend returned them.
      const periods: SchedulingWindowPeriod[] = (response.data.available_periods || []).map((period: SchedulingWindowPeriod) => ({
        open: period.open,
        close: period.close,
      }));

      this.setAvailablePeriods(mergeAvailablePeriods(this._availablePeriods, periods));
    } catch (error) {
      console.error('❌ Error loading available periods:', error);
      this.setAvailablePeriods([]);
    } finally {
      this.setWindowLoading(false);
    }
  };

  /** Seeds from a source instance for the resubmit / clone flow. */
  seedFromInstance = action((seed: { datetime?: string; timezone?: string; schedulingWindow?: string }): void => {
    if (seed.schedulingWindow) {
      this.schedulingWindow = seed.schedulingWindow;
    }
    if (seed.datetime) {
      // A resubmit carries a deliberate start time forward, so the operator owns
      // it rather than having it recomputed out from under them.
      this.requestedDatetime = seed.datetime;
      this.intent = 'requested';
    }
    if (seed.timezone) {
      this.timezone = seed.timezone;
    }
  });

  /**
   * Recomputes immediately, bypassing the debounce, and resolves when done.
   *
   * Deliberately imperative: submitting is a one-shot command, not derived
   * state. Needed because the clock is not observable — under `asap` nothing
   * re-triggers the reaction as time passes, so the value the last recomputation
   * produced can be arbitrarily old on a form that sat open, and that same value
   * becomes the recurrence chain's immutable anchor.
   */
  refreshNow = async (): Promise<void> => {
    if (this.intent === 'asap') {
      this.setComputedInstant(new Date());
    }
    await this.runFirstOccurrence(this._firstOccurrenceRequest);
  };

  /** Refreshes the Redux-sourced values; called by the view each render. */
  setContext = (context: ScheduleContext): void => {
    this._context = context;
  };

  /**
   * Resolves once no first-occurrence calculation is in flight.
   *
   * Does NOT wait out the debounce — a caller that has just changed an input
   * must let the reaction fire first. Exists for tests: recomputation is
   * reactive, so there is no call to await, and `refreshNow` is the production
   * path for anything that needs a fresh value.
   */
  whenSettled = (): Promise<void> => {
    if (this._running === 0) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => this._settledWaiters.push(resolve));
  };

  // ── Private ───────────────────────────────────────────────────────────────

  /** Strips a trailing Z or ±HH:mm so a stored value reads as wall-clock. */
  private static stripOffset(value: string): string {
    return value.replace(/([Zz]|[+-]\d{2}:\d{2})$/, '');
  }

  /**
   * The reaction's effect: asks the backend for the first occurrence and folds
   * the answer back in.
   *
   * Under `requested` the answer is only compared to the operator's value and a
   * mismatch is reported. Under `asap` it becomes the start datetime — which is
   * why it writes `_computedInstant` rather than the operator's field.
   *
   * Eligible periods are always MERGED. The list is cleared explicitly when the
   * window changes, so merging into an empty list is a replace; and because
   * merging is order-independent, this racing with `refreshSchedulingWindow`
   * cannot produce a wrong list.
   */
  private async runFirstOccurrence(request: FirstOccurrenceRequest | null): Promise<void> {
    if (request === null) {
      // Nothing to calculate, so nothing to go stale either: the start is
      // whatever the form says, and no instant is worth reporting.
      this.setPreview({ kind: 'ready', postponed: null, occurrences: [] }, null, null);
      return;
    }

    const { recurrences, schedulingWindow: windowKey, timezone, requestedStart } = request;
    const hasWindow = !!windowKey;
    // Under `requested` the operator's value is the reference to compare
    // against; under `asap` the reference is the clock at the moment we ask.
    const validate = requestedStart !== null;
    const starttime = requestedStart ?? this.nowString();
    // Captured before the request goes out, not when it lands: the answer
    // describes the schedule as it stood when asked, and the round trip can take
    // seconds when a window has to be rescanned.
    const asOf = new Date();

    this._running += 1;
    const seq = ++this._firstOccurrenceSeq;

    try {
      const { earliest, windowEvaluation } = await computeFirstOccurrence({
        recurrences,
        schedulingWindow: windowKey,
        timezone,
        starttime,
      });

      if (seq !== this._firstOccurrenceSeq) {
        return;
      }

      // The backend echoes the window evaluation whenever a window was queried,
      // so the periods repopulate even right after a definition pick.
      if (hasWindow) {
        const incoming = [...mapAvailablePeriods(windowEvaluation)];
        this.setAvailablePeriods(mergeAvailablePeriods(this._availablePeriods, incoming));
      }

      if (earliest === null) {
        this.setPreview({ kind: 'error', message: 'Received an invalid first-occurrence datetime.' }, request, asOf);
        return;
      }

      // Under `asap` the schedule's answer IS the start. Under `requested` the
      // operator's value stands, and only a closed window postpones it — the
      // recurrence does not, since the first instance may begin at any time.
      //
      // `earliest` arrives as an instant and stays one. It used to be formatted to
      // a wall-clock string here and re-parsed downstream, which silently moved it
      // an hour whenever its local reading fell in a repeated DST hour.
      const scheduleAnswer = earliest.toDate();
      const windowClosedAtStart = validate && windowEvaluation?.valid_at_starttime === false;
      const postponedTo = windowClosedAtStart ? this.nextOpeningAtOrAfter(starttime, timezone) : null;

      // available_periods is calendar/display-bounded (~30 days). A closed window
      // with no matching open in that list must not look like "not moved" — submit
      // may still postpone further out, or reject; the preview cannot know which.
      if (windowClosedAtStart && postponedTo === null) {
        this.setPreview(
          {
            kind: 'error',
            message:
              'The scheduling window is closed at the requested time, but no upcoming opening is visible in the preview horizon. Submit may postpone further out or fail if the window stays closed.',
          },
          request,
          asOf
        );
        return;
      }

      // The reference resolved against the zone it was expressed in. Taken from
      // the request's captured values rather than the live form, which may have
      // moved on while this call was out.
      const referenceAt = moment.tz(SubmitScheduleModel.stripOffset(starttime), timezone);
      if (!referenceAt.isValid()) {
        this.setPreview({ kind: 'error', message: 'The requested start is not a valid datetime.' }, request, asOf);
        return;
      }

      const anchor = validate ? (postponedTo ?? referenceAt.toDate()) : scheduleAnswer;
      // A postponement is stated whole or not at all: `postponedTo` is non-null
      // only on the `validate` path, where the reference IS the operator's value.
      const postponed = postponedTo === null ? null : { from: referenceAt.toDate(), to: postponedTo, byWindow: windowKey };

      if (!validate) {
        this.setComputedInstant(scheduleAnswer);
      }

      const occurrences = await this.loadOccurrenceSeries({ anchor, recurrences, windowKey, timezone });
      if (seq !== this._firstOccurrenceSeq) {
        return;
      }

      this.setPreview({ kind: 'ready', postponed, occurrences }, request, asOf);
    } catch (error: unknown) {
      if (seq !== this._firstOccurrenceSeq) {
        return;
      }
      const body = (error as { response?: { data?: { error?: string; code?: string } } })?.response?.data;
      const message = body?.error ?? (error as Error)?.message ?? 'Could not calculate first occurrence.';
      const noOpening = body?.code === NO_OPENING_CODE || message.includes(NO_OPENING_MARKER);
      this.setPreview(noOpening ? { kind: 'unschedulable', message } : { kind: 'error', message }, request, asOf);
    } finally {
      this._running -= 1;
      if (this._running === 0) {
        this._settledWaiters.splice(0).forEach((resolve) => resolve());
      }
    }
  }

  /**
   * Asks for the occurrences following `anchor`. A failure degrades to an empty
   * series: the actual start is the important half of the preview, and losing the
   * rhythm should not blank out the whole panel.
   *
   * That degrade is the only thing left here — the request shape and the
   * string/instant conversion belong to `computeOccurrenceSeries`.
   */
  private async loadOccurrenceSeries({
    anchor,
    recurrences,
    windowKey,
    timezone,
  }: {
    anchor: Date;
    recurrences: readonly Recurrence[];
    windowKey: string;
    timezone: string;
  }): Promise<readonly Date[]> {
    try {
      return await computeOccurrenceSeries({
        firstOccurrence: anchor,
        recurrences,
        schedulingWindow: windowKey,
        timezone,
        count: PREVIEW_OCCURRENCE_COUNT,
      });
    } catch {
      return [];
    }
  }

  /**
   * The first known opening at or after `starttime`, as an instant, or null when
   * the loaded horizon does not reach that far.
   *
   * The periods carry their own offsets, so `open` is already an instant; it is
   * returned as one rather than flattened to a wall-clock string, which would
   * lose an hour for an opening inside a repeated DST hour.
   */
  private nextOpeningAtOrAfter(starttime: string, timezone: string): Date | null {
    const from = moment.tz(starttime, timezone);
    let earliestOpen: moment.Moment | null = null;
    for (const period of this._availablePeriods) {
      const open = moment.utc(period.open).tz(timezone);
      if (open.isSameOrAfter(from) && (!earliestOpen || open.isBefore(earliestOpen))) {
        earliestOpen = open;
      }
    }
    return earliestOpen ? earliestOpen.toDate() : null;
  }

  private nowString(): string {
    return moment.tz(this.effectiveTimezone).format('YYYY-MM-DDTHH:mm:ss');
  }

  private setAvailablePeriods = action((periods: readonly SchedulingWindowPeriod[]): void => {
    this._availablePeriods = periods;
  });

  private setIntent = action((intent: StartIntent): void => {
    this.intent = intent;
  });

  private setComputedInstant = action((value: Date): void => {
    this._computedInstant = value;
  });

  private setRequestedDatetime = action((value: string): void => {
    this.requestedDatetime = value;
  });

  /**
   * Publishes a preview together with the request and the instant it belongs to.
   *
   * All three move as one deliberately: a panel showing one calculation's answer
   * under another's timestamp would be worse than showing no timestamp at all.
   * `asOf` is null when nothing was asked — an intent switch, or a form with
   * nothing to calculate.
   */
  private setPreview = action((preview: SchedulePreview, computedFor: FirstOccurrenceRequest | null, asOf: Date | null): void => {
    this._preview = preview;
    this._previewFor = computedFor;
    this._previewAsOf = asOf;
  });

  private setPreviewTimezone = action((timezone: string | null): void => {
    this._previewTimezone = timezone;
  });

  private setPickedRecurrences = action((recurrences: readonly Recurrence[]): void => {
    this._pickedRecurrences = recurrences;
  });

  private setSchedulingWindow = action((windowKey: string): void => {
    this.schedulingWindow = windowKey;
  });

  private setSelectedRecurrenceId = action((id: string): void => {
    this.selectedRecurrenceId = id;
  });

  private setTimezone = action((timezone: string | null): void => {
    this.timezone = timezone;
  });

  private setWindowLoading = action((loading: boolean): void => {
    this._isWindowLoading = loading;
  });
}
